/**
 * BudgetTransferModal - Modal for transferring freed budget to another investment item
 *
 * Used after decreasing budget on an item - allows transferring the freed amount
 * to another existing investment item in the same building or creating a new one.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Spinner,
  ArrowBendDownRight,
  CurrencyEur,
  MagnifyingGlass,
  Plus,
  Buildings,
  Tag,
  User,
  CalendarBlank,
  X,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useUsers } from "shared/contexts/users.context";
import { getUserName } from "shared/utils/users";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import {
  InvestmentItem,
  INVESTMENT_STATUS_LABELS,
  RenovationLabel,
  CreateInvestmentItemDto,
} from "../types/investment";

interface BudgetTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  sourceItem: InvestmentItem;
  freedAmount: number;
}

type TargetMode = "existing" | "new";
type PickerType = "assignee" | null;

// Format currency helper
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// Searchable Label Dropdown Component
interface LabelDropdownProps {
  selectedLabel: RenovationLabel | null;
  availableLabels: RenovationLabel[];
  onLabelSelect: (label: RenovationLabel | null) => void;
  isDisabled?: boolean;
}

const LabelDropdown: React.FC<LabelDropdownProps> = ({
  selectedLabel,
  availableLabels,
  onLabelSelect,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredLabels = availableLabels.filter(
    (label) =>
      label.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      label.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleSelect = (label: RenovationLabel | null) => {
    onLabelSelect(label);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`flex min-h-[40px] w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
          isDisabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
        }`}
      >
        <div className="flex items-center gap-2">
          <Tag className="text-brick-primary h-4 w-4" />
          {selectedLabel ? (
            <span className="truncate">
              {selectedLabel.name} ({selectedLabel.type})
            </span>
          ) : (
            <span className="text-muted-foreground">Selecteer label...</span>
          )}
        </div>
      </button>

      {isOpen && !isDisabled && (
        <div className="bg-popover absolute top-full right-0 left-0 z-50 mt-1 max-h-64 overflow-hidden rounded-md border shadow-md">
          <div className="border-b p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Zoek label..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="text-muted-foreground hover:bg-accent w-full px-3 py-2 text-left text-sm"
            >
              Geen label
            </button>
            {filteredLabels.map((label) => (
              <button
                key={label.id}
                type="button"
                onClick={() => handleSelect(label)}
                className="hover:bg-accent w-full px-3 py-2 text-left text-sm"
              >
                <span className="font-medium">{label.name}</span>
                <span className="text-muted-foreground ml-2">
                  ({label.type})
                </span>
              </button>
            ))}
            {filteredLabels.length === 0 && (
              <div className="text-muted-foreground px-3 py-2 text-sm">
                Geen labels gevonden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const BudgetTransferModal = ({
  isOpen,
  onClose,
  onSuccess,
  sourceItem,
  freedAmount,
}: BudgetTransferModalProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const { users } = useUsers();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activePicker, setActivePicker] = useState<PickerType>(null);

  // Target selection
  const [targetMode, setTargetMode] = useState<TargetMode>("existing");
  const [transferAmount, setTransferAmount] = useState(freedAmount.toString());
  const [reason, setReason] = useState("");

  // Existing item selection
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTargetItem, setSelectedTargetItem] =
    useState<InvestmentItem | null>(null);
  const [availableItems, setAvailableItems] = useState<InvestmentItem[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState(false);

  // New item form (expanded)
  const [newItemExpanded, setNewItemExpanded] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemDescription, setNewItemDescription] = useState("");
  const [newItemResponsibleUserId, setNewItemResponsibleUserId] = useState("");
  const [newItemLabel, setNewItemLabel] = useState<RenovationLabel | null>(null);
  const [newItemStartDate, setNewItemStartDate] = useState("");
  const [newItemDeadline, setNewItemDeadline] = useState("");

  // Available labels
  const [availableLabels, setAvailableLabels] = useState<RenovationLabel[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTargetMode("existing");
      setTransferAmount(freedAmount.toString());
      setReason("");
      setSearchTerm("");
      setSelectedTargetItem(null);
      setNewItemExpanded(false);
      setNewItemTitle("");
      setNewItemDescription("");
      setNewItemResponsibleUserId(user?.id || "");
      setNewItemLabel(null);
      setNewItemStartDate("");
      setNewItemDeadline("");
    }
  }, [isOpen, freedAmount, user]);

  // Fetch available items for the same building
  useEffect(() => {
    const fetchItems = async () => {
      if (!session || !sourceItem?.building?.id) return;
      setIsLoadingItems(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/items?buildingId=${sourceItem.building.id}&limit=100`,
          { method: "GET" },
          session
        );
        if (response.ok) {
          const result = await response.json();
          // Filter out the source item and IN_PROJECT items
          const filtered = (result.data || []).filter(
            (item: InvestmentItem) =>
              item.id !== sourceItem.id && item.status !== "IN_PROJECT"
          );
          setAvailableItems(filtered);
        }
      } catch (error) {
        console.error("Error fetching investment items:", error);
      } finally {
        setIsLoadingItems(false);
      }
    };

    if (isOpen) {
      fetchItems();
    }
  }, [isOpen, session, sourceItem]);

  // Fetch available renovation labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/labels`,
          { method: "GET" },
          session
        );
        if (response.ok) {
          const data = await response.json();
          setAvailableLabels(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching renovation labels:", error);
      }
    };
    fetchLabels();
  }, [session]);

  // Filter items by search
  const filteredItems = useMemo(() => {
    if (!searchTerm) return availableItems;
    const lower = searchTerm.toLowerCase();
    return availableItems.filter(
      (item) =>
        item.title.toLowerCase().includes(lower) ||
        item.description?.toLowerCase().includes(lower) ||
        item.id.toString().includes(lower)
    );
  }, [availableItems, searchTerm]);

  // Get user name helper
  const getResponsibleUserName = (userId: string) => {
    if (!userId) return null;
    const userData = getUserName(userId, users);
    return userData?.name || "Gebruiker geselecteerd";
  };

  // Handle assignee selection
  const handleAssigneeSelect = useCallback((assignee: any) => {
    setNewItemResponsibleUserId(assignee?.id || "");
    setActivePicker(null);
  }, []);

  // Handle submit
  const handleSubmit = async () => {
    // Validate
    const amount = parseFloat(transferAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Voer een geldig bedrag in");
      return;
    }
    if (amount > freedAmount) {
      toast.error(`Bedrag mag niet hoger zijn dan ${formatCurrency(freedAmount)}`);
      return;
    }
    if (!reason.trim() || reason.trim().length < 5) {
      toast.error("Reden moet minimaal 5 karakters bevatten");
      return;
    }

    if (targetMode === "existing" && !selectedTargetItem) {
      toast.error("Selecteer een investeringslijn");
      return;
    }

    if (targetMode === "new") {
      if (!newItemTitle.trim()) {
        toast.error("Titel is verplicht voor nieuw item");
        return;
      }
      if (!newItemDescription.trim()) {
        toast.error("Beschrijving is verplicht voor nieuw item");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload: any = {
        amount,
        reason: reason.trim(),
      };

      if (targetMode === "existing") {
        payload.targetItemId = selectedTargetItem!.id;
      } else {
        // Create new item data
        const newItemData: CreateInvestmentItemDto = {
          title: newItemTitle.trim(),
          description: newItemDescription.trim(),
          buildingId: sourceItem.building?.id || sourceItem.buildingId || "",
          estimatedCostExclVat: amount, // Will be overwritten to match transfer
          responsibleUserId: newItemResponsibleUserId || user?.id || "",
          labelId: newItemLabel?.id || null,
          estimatedStartDate: newItemStartDate || null,
          estimatedDeadline: newItemDeadline || null,
        };
        payload.newItemData = newItemData;
      }

      const response = await fetchWithAuth(
        `${API_URL}/investment/items/${sourceItem.id}/transfer-budget`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        session
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.message || "Failed to transfer budget");
      }

      toast.success(
        `${formatCurrency(amount)} overgezet naar ${targetMode === "existing" ? selectedTargetItem?.title : newItemTitle}`
      );
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error transferring budget:", error);
      toast.error(
        error instanceof Error ? error.message : "Fout bij overzetten van budget"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const showMainDialog = isOpen && activePicker === null;

  return (
    <>
      <Dialog open={showMainDialog} onOpenChange={handleClose}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowBendDownRight className="h-5 w-5 text-purple-600" />
              Budget overzetten
            </DialogTitle>
            <DialogDescription>
              Zet het vrijgekomen budget over naar een andere investeringslijn voor
              hetzelfde gebouw.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Source Info */}
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
              <div className="text-sm text-purple-600 mb-1">Bronitem</div>
              <div className="font-medium text-gray-900">{sourceItem.title}</div>
              <div className="mt-2 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1 text-gray-600">
                  <Buildings className="h-4 w-4" />
                  {sourceItem.building?.id}
                </div>
                <div className="flex items-center gap-1 font-medium text-purple-700">
                  <CurrencyEur className="h-4 w-4" />
                  {formatCurrency(freedAmount)} beschikbaar
                </div>
              </div>
            </div>

            {/* Transfer Amount */}
            <div className="space-y-2">
              <Label htmlFor="transferAmount">Bedrag om over te zetten *</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  €
                </span>
                <Input
                  id="transferAmount"
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  className="pl-7"
                  min={0.01}
                  max={freedAmount}
                  step={100}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            {/* Target Mode Selection */}
            <div className="space-y-2">
              <Label>Doel</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={targetMode === "existing" ? "default" : "outline"}
                  onClick={() => setTargetMode("existing")}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  <MagnifyingGlass className="mr-2 h-4 w-4" />
                  Bestaand item
                </Button>
                <Button
                  type="button"
                  variant={targetMode === "new" ? "default" : "outline"}
                  onClick={() => {
                    setTargetMode("new");
                    setNewItemExpanded(true);
                  }}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Nieuw item
                </Button>
              </div>
            </div>

            {/* Existing Item Selection */}
            {targetMode === "existing" && (
              <div className="space-y-2">
                <Label>Selecteer investeringslijn</Label>
                <div className="relative">
                  <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Zoek op titel of ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border bg-gray-50">
                  {isLoadingItems ? (
                    <div className="flex items-center justify-center p-4">
                      <Spinner className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                  ) : filteredItems.length === 0 ? (
                    <div className="p-4 text-center text-sm text-gray-500">
                      Geen andere investeringslijnen gevonden voor dit gebouw
                    </div>
                  ) : (
                    filteredItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSelectedTargetItem(item)}
                        className={`w-full p-3 text-left border-b last:border-0 transition-colors ${
                          selectedTargetItem?.id === item.id
                            ? "bg-moss-primary/10 border-moss-primary"
                            : "hover:bg-gray-100"
                        }`}
                        disabled={isSubmitting}
                      >
                        <div className="font-medium text-gray-900">
                          #{item.id} - {item.title}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                          <span>{INVESTMENT_STATUS_LABELS[item.status]}</span>
                          <span>|</span>
                          <span>{formatCurrency(item.estimatedCostExclVat)}</span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* New Item Form */}
            {targetMode === "new" && (
              <div className="space-y-3 rounded-lg border p-4 bg-gray-50">
                <button
                  type="button"
                  onClick={() => setNewItemExpanded(!newItemExpanded)}
                  className="flex w-full items-center justify-between text-left"
                >
                  <span className="font-medium text-gray-700">
                    Nieuwe investeringslijn aanmaken
                  </span>
                  {newItemExpanded ? (
                    <CaretUp className="h-4 w-4 text-gray-500" />
                  ) : (
                    <CaretDown className="h-4 w-4 text-gray-500" />
                  )}
                </button>

                {newItemExpanded && (
                  <div className="space-y-3 pt-2">
                    {/* Title */}
                    <div className="space-y-1">
                      <Label htmlFor="newItemTitle" className="text-sm">
                        Titel *
                      </Label>
                      <Input
                        id="newItemTitle"
                        value={newItemTitle}
                        onChange={(e) => setNewItemTitle(e.target.value)}
                        placeholder="Bijv. Dakisolatie gebouw A"
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Description */}
                    <div className="space-y-1">
                      <Label htmlFor="newItemDescription" className="text-sm">
                        Beschrijving *
                      </Label>
                      <Textarea
                        id="newItemDescription"
                        value={newItemDescription}
                        onChange={(e) => setNewItemDescription(e.target.value)}
                        placeholder="Beschrijf de investering..."
                        rows={2}
                        disabled={isSubmitting}
                      />
                    </div>

                    {/* Label */}
                    <div className="space-y-1">
                      <Label className="text-sm">Renovatielabel</Label>
                      <LabelDropdown
                        selectedLabel={newItemLabel}
                        availableLabels={availableLabels}
                        onLabelSelect={setNewItemLabel}
                        isDisabled={isSubmitting}
                      />
                    </div>

                    {/* Responsible User */}
                    <div className="space-y-1">
                      <Label className="text-sm">Verantwoordelijke</Label>
                      <Button
                        variant="outline"
                        onClick={() => setActivePicker("assignee")}
                        className="w-full justify-start"
                        type="button"
                        disabled={isSubmitting}
                      >
                        <User className="mr-2 h-4 w-4 text-blue-500" />
                        {newItemResponsibleUserId ? (
                          <span>
                            {getResponsibleUserName(newItemResponsibleUserId)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            Selecteer verantwoordelijke...
                          </span>
                        )}
                      </Button>
                    </div>

                    {/* Date Fields */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Startdatum</Label>
                        <div className="relative">
                          <CalendarBlank className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="date"
                            value={newItemStartDate}
                            onChange={(e) => setNewItemStartDate(e.target.value)}
                            className={`pl-10 ${newItemStartDate ? "pr-8" : ""}`}
                            disabled={isSubmitting}
                          />
                          {newItemStartDate && (
                            <button
                              type="button"
                              onClick={() => setNewItemStartDate("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-gray-100"
                            >
                              <X className="h-3 w-3 text-gray-400" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Deadline</Label>
                        <div className="relative">
                          <CalendarBlank className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="date"
                            value={newItemDeadline}
                            onChange={(e) => setNewItemDeadline(e.target.value)}
                            className={`pl-10 ${newItemDeadline ? "pr-8" : ""}`}
                            disabled={isSubmitting}
                          />
                          {newItemDeadline && (
                            <button
                              type="button"
                              onClick={() => setNewItemDeadline("")}
                              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:bg-gray-100"
                            >
                              <X className="h-3 w-3 text-gray-400" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-md bg-purple-100 p-2 text-xs text-purple-700">
                      Het budget wordt automatisch ingesteld op{" "}
                      {formatCurrency(parseFloat(transferAmount) || freedAmount)}.
                      Status wordt IN_BEOORDELING.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reden voor overzetting *</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Beschrijf waarom dit budget wordt overgezet..."
                rows={2}
                disabled={isSubmitting}
              />
              <div className="text-xs text-gray-500">
                Minimaal 5 karakters. Dit wordt opgeslagen in de audit trail van
                het doelitem.
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Annuleren
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                !reason.trim() ||
                reason.trim().length < 5 ||
                (targetMode === "existing" && !selectedTargetItem) ||
                (targetMode === "new" &&
                  (!newItemTitle.trim() || !newItemDescription.trim()))
              }
              className="bg-purple-600 hover:bg-purple-700"
            >
              {isSubmitting ? (
                <>
                  <Spinner className="mr-2 h-4 w-4 animate-spin" />
                  Bezig...
                </>
              ) : (
                <>
                  <ArrowBendDownRight className="mr-2 h-4 w-4" />
                  Budget overzetten
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignee Picker */}
      {activePicker === "assignee" && (
        <UniversalPickerModal
          isOpen={true}
          onClose={() => setActivePicker(null)}
          mode="assignee"
          title="Selecteer verantwoordelijke"
          onConfirm={handleAssigneeSelect}
        />
      )}
    </>
  );
};

export default BudgetTransferModal;
